'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import type { ConnectionState, RetryController } from 'elowen-plugin-shared/retry';
import { createReconnectController } from '../../lib/reconnect';
import { subscribeRevive } from '../../lib/useRevive';
import { readDaemonStream } from '../../lib/daemonStream';
import { getConnectionPolicy, loadConnectionPolicy } from '../../lib/elowenClient';
import { AUTH_CLEARED_EVENT, authGeneration, subscribeAuthTransitions } from '../../lib/token';
import { randomId } from 'elowen-plugin-shared/randomId';
import type { DesktopStreamState } from 'elowen-plugin-ui-kit';

const common = { revision: z.number().int().nonnegative(), reason: z.string().max(256).optional() };
const controlSchema = z.discriminatedUnion('kind', [
  z.object({ ...common, kind: z.literal('user'), holderUserId: z.number().int().positive(), expiresAt: z.number().positive() }),
  z.object({ ...common, kind: z.enum(['agent', 'handover-pending', 'unclaimed']) }),
]);
const faviconSchema = z.object({ favicon: z.string().max(40 * 1024).startsWith('data:image/').nullable() });
// Browser sends `{action, target}`; Sandbox adds an id and a status per operation. Everything beyond `action` is optional.
const fraction = z.number().min(0).max(1);
const actionSchema = z.object({
  action: z.string().min(1).max(128),
  target: z.string().max(4096).optional(),
  id: z.string().min(1).max(128).optional(),
  status: z.enum(['started', 'done', 'failed']).optional(),
  label: z.string().max(4096).optional(),
  text: z.string().max(1024).optional(),
  key: z.string().max(256).optional(),
  direction: z.string().max(32).optional(),
  point: z.object({ x: fraction, y: fraction }).optional(),
  step: z.object({ index: z.number().int().nonnegative(), total: z.number().int().positive() }).optional(),
  message: z.string().max(4096).optional(),
});
/** What an operation announced when it started and its `done`/`failed` does not repeat: that event reports the
 *  outcome, so the text, key and direction it acted with would otherwise vanish from the line the moment it ends. */
const STARTED_FIELDS = ['label', 'text', 'key', 'direction', 'point', 'step'] as const;
const initialState: Omit<DesktopStreamState, 'connectionState' | 'retryConnection'> = {
  favicon: null, control: { state: 'agent', controlRevision: 0 }, action: null,
  connected: false, hasControlSnapshot: false, closed: false, error: null,
};

export function useDesktopStream(path: string | undefined, enabled = true): DesktopStreamState {
  const [state, setState] = useState(initialState);
  const [connectionState, setConnectionState] = useState<ConnectionState | null>(null);
  const retryRef = useRef<(() => void) | null>(null);
  const retryConnection = useCallback(() => retryRef.current?.(), []);
  const identity = useRef(path);
  if (identity.current !== path) {
    identity.current = path;
    setState(initialState);
    setConnectionState(null);
  }
  useEffect(() => {
    // A suspended stream cannot attest to control authority. Keep its last observation, not a live holder;
    // each new authenticated fetch establishes its own revision ordering epoch, including after daemon boot.
    setConnectionState(null);
    setState(value => enabled ? { ...value, connected: false, hasControlSnapshot: false }
      : { ...value, connected: false, hasControlSnapshot: false, control: { state: 'agent', controlRevision: value.control.controlRevision } });
    if (!path || !enabled) return;
    const lifetime = new AbortController();
    const issuedIn = authGeneration();
    const authority = { kind: 'local-owner', id: randomId() } as const;
    const current = () => !lifetime.signal.aborted && issuedIn === authGeneration();
    const cancel = () => {
      lifetime.abort();
      setConnectionState(null);
      setState(value => ({ ...value, connected: false, hasControlSnapshot: false }));
    };
    const offAuth = subscribeAuthTransitions(cancel);
    window.addEventListener(AUTH_CLEARED_EVENT, cancel);
    let reconnect: RetryController<void> | undefined;
    let exhausted = false;
    let revision = 0;
    let terminal = false;
    const reportFailure = (error: unknown) => {
      if (!current()) return;
      setState(value => ({ ...value, connected: false, hasControlSnapshot: false, error: error instanceof Error ? error.message : String(error) }));
      setConnectionState(value => value ? { ...value, freshness: 'stale' } : null);
    };
    const apply = (event: string, text: string): boolean => {
      const raw: unknown = JSON.parse(text);
      if (event === 'control') {
        const data = controlSchema.parse(raw);
        if (data.revision < revision) return false;
        revision = data.revision;
        setState(value => ({
          ...value, connected: true, hasControlSnapshot: true, closed: false, error: null,
          control: { state: data.kind, reason: data.reason, controlRevision: data.revision },
        }));
        return true;
      } else if (event === 'favicon') {
        const data = faviconSchema.parse(raw);
        setState((value) => ({ ...value, favicon: data.favicon }));
      } else if (event === 'action') {
        const data = actionSchema.parse(raw);
        const { action: kind, ...rest } = data;
        setState((value) => {
          const earlier = value.action && rest.id !== undefined && value.action.id === rest.id ? value.action : null;
          const carried = earlier ? Object.fromEntries(STARTED_FIELDS.filter((field) => earlier[field] !== undefined).map((field) => [field, earlier[field]])) : {};
          return { ...value, action: { ...carried, kind, ...rest, receivedAt: Date.now() } };
        });

      } else if (event === 'closed') {
        z.object({ reason: z.string().max(4096) }).parse(raw);
        terminal = true;
        setState((value) => ({ ...value, connected: false, closed: true }));
      }
      return false;
    };
    let starting = false;
    const start = async (restart: boolean): Promise<void> => {
      if (!current() || starting) return;
      starting = true;
      try {
        await loadConnectionPolicy(lifetime.signal);
        if (!current()) return;
        reconnect ??= createReconnectController<void>({
          target: { kind: 'desktop-control', id: path }, getPolicy: getConnectionPolicy, signal: lifetime.signal,
          onProgress: progress => {
            if (!current()) return;
            exhausted = progress.phase === 'exhausted';
            const ready = progress.phase === 'succeeded';
            setConnectionState({
              target: progress.target, generation: progress.generation, sequence: progress.sequence,
              transport: ready ? 'connected' : progress.phase === 'attempting' ? 'connecting' : 'disconnected',
              readiness: ready ? 'ready' : 'pending', retry: progress, observedAt: Date.now(), authority, freshness: 'fresh',
            });
            setState(value => ({ ...value, connected: ready,
              hasControlSnapshot: ready && value.hasControlSnapshot }));
          },
          attempt: async context => {
            if (!current() || context.signal.aborted) return { outcome: 'cancelled', reason: 'aborted' };
            revision = 0;
            const attempt = new AbortController();
            const signal = AbortSignal.any([context.signal, attempt.signal]);
            const result = await readDaemonStream(`/api${path}`, {
              signal, maxBufferLength: 256 * 1024,
              onFrame: frame => {
                if (!current() || signal.aborted) return;
                if (apply(frame.event, frame.data)) context.ready();
                if (terminal) attempt.abort();
              },
            });
            if (terminal) return { outcome: 'blocked', reason: 'closed' };
            return result;
          },
        });
        await (restart ? reconnect.restart() : reconnect.start()).done;
      } finally { starting = false; }
    };
    retryRef.current = () => { if (terminal) return; void start(true).catch(reportFailure); };
    const offRevive = subscribeRevive(({ foregroundReturned }) => {
      if (foregroundReturned && exhausted) retryRef.current?.();
    });
    void start(false).catch(reportFailure);
    return () => {
      cancel();
      offAuth();
      offRevive();
      window.removeEventListener(AUTH_CLEARED_EVENT, cancel);
      retryRef.current = null;
      void reconnect?.stop();
    };
  }, [path, enabled]);
  return { ...state, connectionState, retryConnection };
}
